// src/hooks/useObrasData.js
import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { AuthContext } from '../context/authContext';
import { useSession } from './useSession';

export const useObrasData = (initialFilters = { year: 'Todos', partido: ['Todos'], tipo: 'Todos', subtipo: 'Todos', estado: 'Todos', area_responsable: 'Todos' }) => {
	const { token } = useContext(AuthContext);
	const { authenticatedFetch } = useSession();

	const [filters, setFilters] = useState(initialFilters);
	const [data, setData] = useState({
		metrics: { totalObras: 0, municipiosAlcanzados: 0, presupuestoTotal: 0, beneficiarios: 0 },
		proyectosPorPartido: [],
		proyectosPorEstado: [],
		proyectosPorPrograma: [], // 🎯 Agregado
		proyectosPorAnio: [], // ➔ Súper importante que arranque como array vacío []
		//nueva serie historica
		proyectosPorSubsidioAnual: [],
		recursosPorSubsidioAnual: [],
		distribucionMontos: { conveniado: 0, adendas: 0 },
		loading: true,
		error: null
	});

	// 🔥 NUEVO ESTADO EXCLUSIVO: Para sostener la foto completa del año para el mapa
	const [proyectosPorPartidoBase, setProyectosPorPartidoBase] = useState([]);

	// Tipos de obra que existen realmente para el área seleccionada
	const [tiposPorArea, setTiposPorArea] = useState(null);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	// 🔄 Refetch con datos previos: loading queda false para no desmontar el panel (estilo keepPreviousData de RED)
	const [isRefetching, setIsRefetching] = useState(false);
	const hasLoadedRef = useRef(false);

	// Función para actualizar filtros de forma dinámica
	const updateFilters = useCallback((newFilters) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
	}, []);

	// Función para resetear filtros al estado inicial
	const resetFilters = useCallback(() => {
		setFilters(initialFilters);
	}, [initialFilters]);
	//const abortControllerRef = useRef(null);


	useEffect(() => {
		const fetchTableroData = async () => {
			try {
				// Primera carga: loading=true (desmonta el panel). Refetch: solo isRefetching (mantiene datos previos)
				if (!hasLoadedRef.current) {
					setLoading(true);
				} else {
					setIsRefetching(true);
				}

				// 📡 1. Petición Normal (Filtros dinámicos de la UI)
				const params = new URLSearchParams();
				// Year: soporta string ('2026') o array (['2020','2021'])
				if (filters.year) {
					if (Array.isArray(filters.year)) {
						filters.year.forEach(y => params.append('year', y));
					} else {
						params.append('year', filters.year);
					}
				}
				if (filters.criterioYear) params.append('criterioYear', filters.criterioYear);
				if (filters.partido) {
					// Si es un array de partidos seleccionados, los enviamos correctamente
					if (Array.isArray(filters.partido)) {
						filters.partido.forEach(p => params.append('partido[]', p));
					} else {
						params.append('partido', filters.partido);
					}
				}
				if (filters.tipo) params.append('tipo', filters.tipo);
				if (filters.subtipo) params.append('subtipo', filters.subtipo);
				if (filters.estado) {
					if (Array.isArray(filters.estado)) {
						filters.estado.forEach(e => params.append('estado[]', e));
					} else {
						params.append('estado', filters.estado);
					}
				}
				if (filters.area_responsable) params.append('area', filters.area_responsable);
				if (filters.mes) {
					if (Array.isArray(filters.mes)) {
						filters.mes.forEach(m => params.append('mes', m));
					} else {
						params.append('mes', filters.mes);
					}
				}

				const headers = {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': '*',
					...(token ? { 'Authorization': `Bearer ${token}` } : {})
				}

				const response = await authenticatedFetch(`/api/obras/dashboard?${params.toString()}`, { headers });
				if (!response.ok) throw new Error("Error en la petición de filtros");
				const result = await response.json();

				setData({
					metrics: result.metrics,
					proyectosPorPartido: result.proyectosPorPartido || [],
					proyectosPorEstado: result.proyectosPorEstado || [],
					proyectosPorPrograma: result.proyectosPorPrograma || [],
					proyectosPorAnio: result.proyectosPorAnio || [],
					proyectosPorSubsidioAnual: result.proyectosPorSubsidioAnual || [],
					recursosPorSubsidioAnual: result.recursosPorSubsidioAnual || [],
					distribucionMontos: result.distribucionMontos || { conveniado: 0, adendas: 0 }
				});

				// 🗺️ 2. PETICIÓN TESTIGO EN PARALELO: Trae la foto territorial limpia del año seleccionado
				// Ignoramos el filtro de partido para que el mapa base mute con el año
				const paramsBase = new URLSearchParams();
				if (filters.year) {
					if (Array.isArray(filters.year)) {
						filters.year.forEach(y => paramsBase.append('year', y));
					} else {
						paramsBase.append('year', filters.year);
					}
				}
				if (filters.criterioYear) paramsBase.append('criterioYear', filters.criterioYear);
				if (filters.tipo) paramsBase.append('tipo', filters.tipo);
				if (filters.subtipo) paramsBase.append('subtipo', filters.subtipo);
				if (filters.area_responsable) paramsBase.append('area', filters.area_responsable);
				if (filters.mes) {
					if (Array.isArray(filters.mes)) {
						filters.mes.forEach(m => paramsBase.append('mes', m));
					} else {
						paramsBase.append('mes', filters.mes);
					}
				}
				paramsBase.append('partido', 'Todos'); // Forzamos al backend a darnos la provincia entera

				const responseBase = await authenticatedFetch(`/api/obras/dashboard?${paramsBase.toString()}`, { headers });
				if (responseBase.ok) {
					const resultBase = await responseBase.json();
					// Guardamos los 135 (o municipios con gestión de ese año) de forma segura
					setProyectosPorPartidoBase(resultBase.proyectosPorPartido || []);
				}

				setError(null);
			} catch (err) {
				console.error("CRÍTICO - Error en useObrasData:", err);
				setError(err.message);
			} finally {
				hasLoadedRef.current = true;
				setLoading(false);
				setIsRefetching(false);
			}
		};

		fetchTableroData();

		// 🔥 Se sincroniza con CUALQUIER cambio de filtro, garantizando respuestas exactas
	}, [filters]);

	// 📚 Trae los tipos de obra que existen en la BD para el área responsable seleccionada
	useEffect(() => {
		let cancelado = false;
		const fetchTiposPorArea = async () => {
			try {
				const params = new URLSearchParams();
				if (filters.area_responsable && filters.area_responsable !== 'Todos') {
					params.append('area', filters.area_responsable);
				}
				const headers = {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': '*',
					...(token ? { 'Authorization': `Bearer ${token}` } : {})
				};
				const response = await authenticatedFetch(`/api/obras/dashboard/tipos-por-area?${params.toString()}`, { headers });
				if (!response.ok) throw new Error("Error al obtener tipos por área");
				const result = await response.json();
				if (!cancelado) setTiposPorArea(result.tipos || []);
			} catch (err) {
				console.error("Error en tipos-por-area:", err);
				if (!cancelado) setTiposPorArea([]);
			}
		};
		fetchTiposPorArea();
		return () => { cancelado = true; };
	}, [filters.area_responsable, token, authenticatedFetch]);

	return {
		...data,
		proyectosPorPartidoBase, // 🚀 EXPONEMOS LA FOTO BASE PARA EL MAPA
		tiposPorArea, // 📚 Tipos reales según el área responsable seleccionada
		//distribucionMontos,
		loading,
		isRefetching,
		error,
		filters,
		updateFilters,
		resetFilters
	};
};